/**
 * Keeps the favorites store in step with who is signed in.
 *
 * Mounted once at the root, inside both the Redux provider and the auth
 * provider. Being an effect is load-bearing twice over: effects never run
 * during a server render, so this is also the guarantee that no account data
 * reaches the SSR output, and that the first client render matches it.
 */

import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useAuth } from 'lib/auth/auth-context'
import { favoritesActions } from 'store/favorites.reducer'
import { loadFavorites } from 'services/favorites.service'

export function useFavoritesSync(): void {
   const { state } = useAuth()
   const dispatch = useDispatch()

   const uid = state.status === 'signed-in' ? state.user.uid : null

   useEffect(() => {
      if (uid) {
         void loadFavorites(dispatch)
         return
      }
      dispatch(favoritesActions.cleared())
      // Keyed on the uid, not just the status: signing out of one account and
      // into another inside a single SPA session never passes through
      // 'signed-out' as a rendered state, so a status-only dependency would
      // leave the first account's words in the store.
   }, [uid, dispatch])
}
